import React from 'react';
import styled from 'styled-components/macro';
import { Title, Body, Button, Modal, Flex } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import { HorizontalDivider } from 'components/Dividers/Dividers';
import { SchemaAttributesSelection } from '../../components/Schemas/ViewSchemaPage/ProjectRuleset/components/SchemaAttributesSelection';
import { SchemaWithTypes } from '../useSchemaData';
import {
  ConsistencyCollectionView,
  ConsistencySchemaFilterView,
} from '../types';
import {
  ReportData,
  useRunConsistencyAttributesSelection,
} from './useRunConsistencyAttributesSelection';

type RunConsistencyAttributesSelectionProps = {
  visible: boolean;
  loading?: boolean;
  collection?: ConsistencyCollectionView;
  onCancel: () => void;
  onCreateReport: (attributes: ReportData) => void;
  schemaFilters: ConsistencySchemaFilterView[];
  schemaData: SchemaWithTypes[];
};

export const RunConsistencyAttributesSelection = ({
  visible,
  loading = false,
  collection,
  onCancel,
  onCreateReport,
  schemaFilters,
  schemaData,
}: RunConsistencyAttributesSelectionProps) => {
  const {
    schemaEntries,
    reportData,
    hasAttributes,
    selectedSchemaId,
    setSelectedSchemaId,
    resetReport,
    handleSchemaAttributesChange,
    saveReportTemplate,
    templateHasChanges,
    saveLoading,
    shouldShowSaveSection,
    saveResultFeedback,
  } = useRunConsistencyAttributesSelection({
    schemaFilters,
    schemaData,
    reportTemplate: collection?.reportTemplate,
    collection,
  });

  return (
    <StyledModal hideFooter visible={visible} onCancel={onCancel}>
      <StyledHeader>
        <StyledDialogTitle level={3}>
          Create consistency report
        </StyledDialogTitle>
        <Body size="medium">
          Create a report from the consistency check. The result is found in the
          "Data Quality Explorer".
        </Body>
        <Body size="medium">
          Customize the report by adding schema attributes to include as extra
          columns in the failed instances output.
        </Body>
      </StyledHeader>

      <StyledSectionTitle level={4}>
        Select attributes to include in the report file
      </StyledSectionTitle>

      <StyledSchemaList>
        {schemaEntries.map(({ filter, currentSchema, allSchemas }) => (
          <SchemaAttributesSelection
            key={filter.schemaType}
            selectedSchemaId={selectedSchemaId}
            currentSchema={currentSchema}
            schemas={allSchemas}
            attributesList={reportData[filter.schemaType] ?? []}
            setSelectedSchemaId={setSelectedSchemaId}
            onSchemaAttributesChange={handleSchemaAttributesChange}
          />
        ))}
      </StyledSchemaList>

      {shouldShowSaveSection && (
        <StyledSaveReportContainer>
          <StyledDivider />

          <Flex alignItems="center" gap={8}>
            <Button
              disabled={!templateHasChanges}
              loading={saveLoading}
              onClick={() => void saveReportTemplate()}
            >
              Save report settings
            </Button>

            {saveResultFeedback === 'success' && (
              <StyledSaveResult className="success">
                <Icon type="Checkmark" />
                <Body size="small">Report settings successfully saved</Body>
              </StyledSaveResult>
            )}

            {saveResultFeedback === 'error' && (
              <StyledSaveResult className="error">
                <Icon type="Error" />
                <Body size="small">Report settings could not be saved</Body>
              </StyledSaveResult>
            )}
          </Flex>

          <StyledSaveReportNotice>
            <Icon type="Info" />
            <StyledNoticeText size="small">
              Saved report settings affect the reporting of this collection for
              all users.
            </StyledNoticeText>
          </StyledSaveReportNotice>
        </StyledSaveReportContainer>
      )}

      <StyledFooter>
        <Button
          size="small"
          icon="Delete"
          aria-label="Reset all attributes"
          disabled={!hasAttributes}
          onClick={resetReport}
        >
          Reset all attributes
        </Button>
        <StyledButtonGroup>
          <Button type="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => onCreateReport(reportData)}
          >
            Run and create report
          </Button>
        </StyledButtonGroup>
      </StyledFooter>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  overflow: auto;
  width: 760px;
  max-width: calc(90% - 2rem);
  .cogs-modal-content {
    padding: 0;
    min-height: 200px;
    position: relative;
    overflow-wrap: break-word;
    margin: 1rem 2rem;
  }
  .cogs-icon-Close {
    cursor: pointer;
  }
`;

const StyledHeader = styled.div`
  margin-bottom: 2rem;
`;

const StyledDialogTitle = styled(Title)`
  margin: 1rem 0;
`;

const StyledSchemaList = styled.div`
  min-height: 20vh;
  overflow: auto;
`;

const StyledSectionTitle = styled(Title)`
  margin: 1rem 0;
`;

const StyledFooter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const StyledSaveReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 40px;
`;

const StyledDivider = styled(HorizontalDivider)`
  margin: 8px 0;
`;

const StyledSaveResult = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;

  &.error {
    .cogs-icon,
    .cogs-body--small {
      color: var(--cogs-text-warning);
    }
  }

  &.success {
    .cogs-icon,
    .cogs-body--small {
      color: var(--cogs-graphics-success);
    }
  }
`;

const StyledSaveReportNotice = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const StyledNoticeText = styled(Body)`
  color: var(--cogs-primary);
`;
